// @ts-nocheck

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import styled, { keyframes } from 'styled-components';

import Switch from '../../../components/common/Switch';
import HelperButton from '../../../components/HelperButton';
import InfosLabel from '../../../components/InfosLabel';
import Modal from '../../../components/Modal';
import { ModalContentSpacing, ModalFormat } from '../../../constants/ui/modal';
import { useViewportContext } from '../../../contexts/ViewportContext';
import { getPortalRootDOMElement } from '../../../DOMElements';
import theme from '../../../styled-components/dark';
import { SwitchIconImg } from '../../../styled-components/shared';
import KLBasketIcon from '../assets/img/kl_basket_toggle_icon.png';

const INFOS_LABEL_TIMEOUT_APPEARANCE = 300; // ms

const appearAnim = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const Wrap = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;

  .switch-label {
    white-space: nowrap;
  }
`;

const StyledHelperButton = styled(HelperButton)`
  margin-left: 0.4em;
  font-size: 90%;
`;

const SwitchWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

const HelperWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

const HelperTitle = styled.h3`
  margin: 0;
  text-align: center;
  font-weight: normal;
  margin-bottom: 0.5em;
`;

const HelperText = styled.p`
  margin: 0;
  color: ${theme.BasketFreebetHelper.color};
  white-space: pre-line;
`;

const StyledInfosLabel = styled(InfosLabel)`
  &.infos-label-transitions-enter {
    animation: ${appearAnim} ${INFOS_LABEL_TIMEOUT_APPEARANCE}ms ease-out forwards;
  }

  &.infos-label-transitions-exit {
    animation: ${appearAnim} ${INFOS_LABEL_TIMEOUT_APPEARANCE}ms ease-out forwards reverse;
  }
`;

const KLBasketIconComponent = (props) => <SwitchIconImg {...props} src={KLBasketIcon} />;

type Props = {
  className?: string;
  toggled: boolean;
  onToggle: (next: boolean) => void;
  disabled: boolean;
  label?: string;
  infosLabel?: string;
};

const KLBasketSwitch = ({ className, toggled = false, label = 'Koh-Lanta', onToggle, disabled, infosLabel }: Props) => {
  const isRendered = useRef(false);
  const justClicked = useRef(false);
  const onToggleRef = useRef(onToggle);
  const [showInfosLabel, setShowInfosLabel] = useState(false);
  const { width, height } = useViewportContext();
  const shouldDisplayInfosInModal = useMemo(() => height / width < 2, [height, width]);

  useEffect(() => {
    onToggleRef.current = onToggle;
  }, [onToggle]);

  const onClick = useCallback(() => {
    if (!disabled) {
      // Circular behaviour allowing the switch to auto trigger onToggle when anything changes but user didn't clicked
      // See the next useEffect
      justClicked.current = true;
      onToggle(!toggled);
    }
  }, [disabled, onToggle, toggled]);

  useEffect(() => {
    if (!toggled && !justClicked.current && typeof onToggleRef.current === 'function') {
      onToggleRef.current(true);
    }
    justClicked.current = false;
  }, [toggled]);

  useEffect(() => {
    if (!isRendered.current) {
      onToggle(true);
      isRendered.current = true;
    }
  }, [onToggle]);

  const onHelperButtonClick = useCallback(() => {
    setShowInfosLabel((prev) => !prev);
  }, []);

  return (
    <Wrap className={className}>
      <SwitchWrapper>
        <Switch
          PrimaryIcon={KLBasketIconComponent}
          toggled={toggled}
          label={label}
          onToggle={onClick}
          disabled={disabled}
        />
        {infosLabel ? (
          <StyledHelperButton toggled={showInfosLabel} onToggle={onHelperButtonClick} symbol={'i'} />
        ) : null}
      </SwitchWrapper>

      <CSSTransition
        in={!!infosLabel && showInfosLabel && !shouldDisplayInfosInModal}
        timeout={INFOS_LABEL_TIMEOUT_APPEARANCE}
        classNames="infos-label-transitions"
        unmountOnExit
        mountOnEnter
      >
        <StyledInfosLabel symbol="i" label={infosLabel} />
      </CSSTransition>

      {!!infosLabel && showInfosLabel && shouldDisplayInfosInModal
        ? createPortal(
            <Modal
              backgroundColor={theme.BasketFreebetHelper.modalBackgroundColor}
              closeOnEsc
              closeOnOutsideClick
              contentSpacing={ModalContentSpacing.SMALL}
              format={ModalFormat.XSMALL}
              hasBorderRadius
              hasCloseButton
              hasOverlay
              onClose={onHelperButtonClick}
              blockHeight
            >
              <HelperWrapper>
                <HelperTitle>Non éligible</HelperTitle>
                <HelperText>{infosLabel}</HelperText>
              </HelperWrapper>
            </Modal>,
            getPortalRootDOMElement(),
          )
        : null}
    </Wrap>
  );
};

export default KLBasketSwitch;
